import React, { useMemo, useState } from "react";
import { Select, Spin, Table } from "antd";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis } from "recharts";
import { useGetEventsQuery } from "../../services/events";
import { useGetEventComparisonQuery } from "../../services/analytics";

const { Option } = Select;

const pieColors = ["#FF9F7A", "#57C5B6", "#91CC75", "#5470C6", "#EE6666", "#73C0DE", "#FAC858"];

const EventSegmentation = () => {
  const [segmentBy, setSegmentBy] = useState("region"); // 'region' or 'scope'

  const { data: eventsData, isLoading: isLoadingEvents } = useGetEventsQuery({
    currentPage: 1,
    resultPerPage: 1000,
  });

  const events = eventsData?.events?.data || [];
  const allEventIds = events.map((e) => e._id).join(",");

  const {
    data: comparisonData,
    isFetching: isLoadingComparison,
  } = useGetEventComparisonQuery(allEventIds, { skip: !allEventIds });

  const tableData = comparisonData?.data || [];

  const grouped = useMemo(() => {
    const keyField = segmentBy === "region" ? "region" : "scope";
    const map = {};
    tableData.forEach((item) => {
      let key = item[keyField];
      if (!key || key === "") key = "Unknown";
      if (segmentBy === "scope" && key) {
        key = key.charAt(0).toUpperCase() + key.slice(1);
      }
      if (!map[key]) {
        map[key] = {
          group: key,
          events: 0,
          totalDelegates: 0,
          totalSponsors: 0,
          totalMeetings: 0,
          totalShowUps: 0,
        };
      }
      map[key].events += 1;
      map[key].totalDelegates += item.totalDelegates || 0;
      map[key].totalSponsors += item.totalSponsors || 0;
      map[key].totalMeetings += item.totalMeetings || 0;
      map[key].totalShowUps += item.totalShowUps || 0;
    });
    const arr = Object.values(map);
    arr.sort((a, b) => b.events - a.events);
    return arr;
  }, [tableData, segmentBy]);

  const pieData = grouped.map((g) => ({ name: g.group, value: g.events }));

  const barData = grouped.map((g) => ({
    name: g.group,
    Delegates: g.totalDelegates,
    Sponsors: g.totalSponsors,
    Meetings: g.totalMeetings,
  }));

  const columns = [
    { title: segmentBy === "region" ? "Region / Location" : "Scope", dataIndex: "group", key: "group" },
    { title: "Events", dataIndex: "events", key: "events", sorter: (a, b) => a.events - b.events },
    {
      title: "Total Delegates",
      dataIndex: "totalDelegates",
      key: "totalDelegates",
      sorter: (a, b) => a.totalDelegates - b.totalDelegates,
    },
    {
      title: "Total Sponsors",
      dataIndex: "totalSponsors",
      key: "totalSponsors",
      sorter: (a, b) => a.totalSponsors - b.totalSponsors,
    },
    {
      title: "Total Meetings",
      dataIndex: "totalMeetings",
      key: "totalMeetings",
      sorter: (a, b) => a.totalMeetings - b.totalMeetings,
    },
    {
      title: "Total Show-ups",
      dataIndex: "totalShowUps",
      key: "totalShowUps",
      sorter: (a, b) => a.totalShowUps - b.totalShowUps,
    },
  ];

  const loading = isLoadingEvents || isLoadingComparison;

  return (
    <div className="animate-fade-in">
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
        <div>
          <span className="mr-3 font-semibold text-gray-700">Segment By:</span>
          <Select
            style={{ width: 260 }}
            value={segmentBy}
            onChange={(val) => setSegmentBy(val)}
          >
            <Option value="region">Region / Location</Option>
            <Option value="scope">Event Scope (Clinical / Drug)</Option>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center p-20">
          <Spin size="large" />
        </div>
      ) : grouped.length === 0 ? (
        <div className="text-center text-gray-400 mt-20">
          No segmentation data available.
        </div>
      ) : (
        <div className="space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" style={{ height: 360 }}>
              <h3 className="font-semibold text-lg text-gray-700 mb-4">
                {segmentBy === "region" ? "Events by Region / Location" : "Events by Scope"}
              </h3>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    label
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${entry.name}-${index}`}
                        fill={pieColors[index % pieColors.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" style={{ height: 360 }}>
              <h3 className="font-semibold text-lg text-gray-700 mb-4">
                Delegates / Sponsors / Meetings by {segmentBy === "region" ? "Region" : "Scope"}
              </h3>
              <ResponsiveContainer>
                <BarChart
                  data={barData}
                  margin={{ top: 20, right: 20, left: 0, bottom: 40 }}
                >
                  <XAxis dataKey="name" angle={-25} textAnchor="end" height={70} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Delegates" fill="#1890ff" />
                  <Bar dataKey="Sponsors" fill="#52c41a" />
                  <Bar dataKey="Meetings" fill="#faad14" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden">
            <Table
              dataSource={grouped}
              columns={columns}
              rowKey="group"
              pagination={{ pageSize: 10, className: "px-4 mb-4" }}
              rowClassName="hover:bg-gray-50/50 transition-colors"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EventSegmentation;

